import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { GooglePlacesAddress } from "@/components/GooglePlacesAddress";
import { 
  MapPin, 
  User, 
  Calendar, 
  Clock, 
  Trash2, 
  Save,
  Building2,
  Mail,
  Phone,
  AlertCircle
} from "lucide-react";
import { toast } from "sonner";

interface JobProfile {
  full_name: string;
  email?: string;
  company_name?: string;
  phone?: string;
}

interface Job {
  id: string;
  job_number: number;
  title: string;
  description: string;
  status: string;
  priority: string;
  location: string;
  budget_min: number | null;
  budget_max: number | null;
  agreed_cost: number | null;
  required_date: string | null;
  required_specialties: string[] | null;
  job_category: string | null;
  created_at: string;
  updated_at: string;
  customer_profile: JobProfile | null;
  tradesman_profile: JobProfile | null;
}

interface AdminJobDetailDialogProps {
  job: Job | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onJobUpdated: () => void;
}

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "quote_accepted", label: "Quote Accepted" },
  { value: "payment_pending", label: "Payment Pending" },
  { value: "assigned", label: "Assigned" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

const PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "standard", label: "Standard" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

export function AdminJobDetailDialog({
  job,
  open,
  onOpenChange,
  onJobUpdated,
}: AdminJobDetailDialogProps) {
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedPriority, setSelectedPriority] = useState("");
  const [selectedTradesmanId, setSelectedTradesmanId] = useState<string | null>(null);
  const [agreedCost, setAgreedCost] = useState("");
  const [requiredDate, setRequiredDate] = useState("");
  const [jobCategory, setJobCategory] = useState("");
  const [requiredSpecialties, setRequiredSpecialties] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [jobLocation, setJobLocation] = useState("");
  const [tradesmen, setTradesmen] = useState<any[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (job && open) {
      setSelectedStatus(job.status);
      setSelectedPriority(job.priority);
      setSelectedTradesmanId(job.tradesman_profile ? "assigned" : null);
      setAgreedCost(job.agreed_cost?.toString() || "");
      setRequiredDate(job.required_date ? new Date(job.required_date).toISOString().split('T')[0] : "");
      setJobCategory(job.job_category || "");
      setRequiredSpecialties(job.required_specialties?.join(", ") || "");
      setJobTitle(job.title);
      setJobDescription(job.description);
      setJobLocation(job.location);
      fetchTradesmen();
    }
  }, [job, open]);

  const fetchTradesmen = async () => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, company_name")
        .eq("user_type", "tradesman")
        .eq("is_suspended", false)
        .eq("insurance_approved", true)
        .order("full_name");
      
      if (error) throw error;
      setTradesmen(data || []);
    } catch (error) {
      console.error("Error fetching tradesmen:", error);
    }
  };

  if (!job) return null;

  const formatCurrency = (amount: number | null) => {
    if (!amount) return "Not set";
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleStatusUpdate = async () => {
    if (!job) return;

    setIsUpdating(true);
    try {
      const updates: any = {
        status: selectedStatus,
        priority: selectedPriority,
        updated_at: new Date().toISOString(),
      };

      // Handle tradesman assignment
      if (selectedTradesmanId === "unassign") {
        updates.tradesman_id = null;
      } else if (selectedTradesmanId && selectedTradesmanId !== "assigned") {
        updates.tradesman_id = selectedTradesmanId;
      }

      // Handle agreed cost
      if (agreedCost) {
        const cost = parseFloat(agreedCost);
        if (!isNaN(cost)) {
          updates.agreed_cost = cost;
        }
      } else {
        updates.agreed_cost = null;
      }

      // Handle required date
      if (requiredDate) {
        updates.required_date = new Date(requiredDate).toISOString();
      } else {
        updates.required_date = null;
      }

      // Handle job category
      if (jobCategory) {
        updates.job_category = jobCategory;
      } else {
        updates.job_category = null;
      }

      // Handle required specialties
      if (requiredSpecialties.trim()) {
        updates.required_specialties = requiredSpecialties.split(",").map(s => s.trim()).filter(Boolean);
      } else {
        updates.required_specialties = null;
      }

      // Handle basic job information
      if (jobTitle.trim()) {
        updates.title = jobTitle.trim();
      }
      if (jobDescription.trim()) {
        updates.description = jobDescription.trim();
      }
      if (jobLocation.trim()) {
        updates.location = jobLocation.trim();
      }

      const { error } = await supabase
        .from("jobs")
        .update(updates)
        .eq("id", job.id);

      if (error) throw error;

      // Send assignment notification if tradesman was assigned
      if (selectedTradesmanId && selectedTradesmanId !== "assigned" && selectedTradesmanId !== "unassign") {
        try {
          await supabase.functions.invoke('notify-tradesman-assignment', {
            body: {
              job_id: job.id,
              tradesman_id: selectedTradesmanId,
              customer_id: (job as any).customer_id,
              agreed_cost: updates.agreed_cost
            }
          });
          console.log("Assignment notification sent successfully");
        } catch (notificationError) {
          console.error("Failed to send assignment notification:", notificationError);
          // Don't fail the main operation if notification fails
        }
      }

      toast.success("Job updated successfully");
      onJobUpdated();
    } catch (error) {
      console.error("Error updating job:", error);
      toast.error("Failed to update job");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteJob = async () => {
    if (!job || !confirm("Are you sure you want to delete this job? This action cannot be undone.")) {
      return;
    }

    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from("jobs")
        .delete()
        .eq("id", job.id);

      if (error) throw error;

      toast.success("Job deleted successfully");
      onOpenChange(false);
      onJobUpdated();
    } catch (error) {
      console.error("Error deleting job:", error);
      toast.error("Failed to delete job");
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending": return "bg-yellow-100 text-yellow-800";
      case "quote_accepted": return "bg-purple-100 text-purple-800";
      case "payment_pending": return "bg-orange-100 text-orange-800";
      case "assigned": return "bg-blue-100 text-blue-800";
      case "in_progress": return "bg-indigo-100 text-indigo-800";
      case "completed": return "bg-green-100 text-green-800";
      case "cancelled": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent": return "bg-red-100 text-red-800";
      case "high": return "bg-orange-100 text-orange-800";
      case "standard": return "bg-blue-100 text-blue-800";
      case "low": return "bg-gray-100 text-gray-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto mx-2 sm:mx-auto"
        onPointerDownOutside={(e) => {
          const t = (e as any).target as HTMLElement | null;
          const o = (e as any).detail?.originalEvent?.target as HTMLElement | null;
          const target = t || o;
          if (target && (target.closest('.pac-container') || target.classList?.contains('pac-item'))) {
            e.preventDefault();
          }
        }}
        onInteractOutside={(e) => {
          const t = (e as any).target as HTMLElement | null;
          const o = (e as any).detail?.originalEvent?.target as HTMLElement | null;
          const target = t || o;
          if (target && (target.closest('.pac-container') || target.classList?.contains('pac-item'))) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>Job #{job.job_number} - {job.title}</span>
            <Badge className={getStatusColor(job.status)}>
              {job.status.replace('_', ' ')}
            </Badge>
            <Badge className={getPriorityColor(job.priority)}>
              {job.priority}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Job Information */}
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Description</Label>
              <p className="text-sm mt-1 p-3 bg-muted rounded-md">{job.description}</p>
            </div>

            <div className="flex items-center gap-2 text-sm">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span>{job.location}</span>
            </div>

            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span>Created: {formatDate(job.created_at)}</span>
            </div>

            <div className="flex items-center gap-2 text-sm">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span>Last Updated: {formatDate(job.updated_at)}</span>
            </div>

            {job.required_date && (
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span>Required Date: {formatDate(job.required_date)}</span>
              </div>
            )}

            {job.job_category && (
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Category</Label>
                <p className="text-sm mt-1">{job.job_category}</p>
              </div>
            )}

            {job.required_specialties && job.required_specialties.length > 0 && (
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Required Specialties</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {job.required_specialties.map((specialty, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {specialty}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* People & Budget */}
          <div className="space-y-4">
            {/* Customer */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <User className="h-4 w-4" />
                <Label className="font-medium">Customer</Label>
              </div>
              {job.customer_profile ? (
                <div className="space-y-2 text-sm">
                  <p className="font-medium">{job.customer_profile.full_name}</p>
                  {job.customer_profile.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-3 w-3 text-muted-foreground" />
                      <span>{job.customer_profile.email}</span>
                    </div>
                  )}
                  {job.customer_profile.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-3 w-3 text-muted-foreground" />
                      <span>{job.customer_profile.phone}</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">Unknown customer</p>
              )}
            </div>

            {/* Tradesman */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="h-4 w-4" />
                <Label className="font-medium">Tradesman</Label>
              </div>
              {job.tradesman_profile ? (
                <div className="space-y-2 text-sm">
                  <p className="font-medium">{job.tradesman_profile.full_name}</p>
                  {job.tradesman_profile.company_name && (
                    <p className="text-muted-foreground">{job.tradesman_profile.company_name}</p>
                  )}
                  {job.tradesman_profile.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-3 w-3 text-muted-foreground" />
                      <span>{job.tradesman_profile.email}</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">Not assigned</p>
              )}
            </div>

            {/* Budget */}
            <div className="p-4 border rounded-lg">
              <Label className="font-medium mb-2 block">Budget</Label>
              <div className="space-y-2 text-sm">
                {job.agreed_cost ? (
                  <p className="font-medium text-green-600">
                    Agreed: {formatCurrency(job.agreed_cost)}
                  </p>
                ) : (
                  <>
                    {job.budget_min && (
                      <p>Minimum: {formatCurrency(job.budget_min)}</p>
                    )}
                    {job.budget_max && (
                      <p>Maximum: {formatCurrency(job.budget_max)}</p>
                    )}
                    {!job.budget_min && !job.budget_max && (
                      <p className="text-muted-foreground">Not specified</p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <Separator />

        {/* Admin Controls */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-500" />
            <Label className="font-medium text-lg">Administrator Controls</Label>
          </div>
          <p className="text-sm text-muted-foreground">
            As an administrator, you have full control over all job settings. Changes will take effect immediately.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="jobTitle">Job Title</Label>
              <Input
                id="jobTitle"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                placeholder="Enter job title"
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="jobDescription">Job Description</Label>
              <Textarea
                id="jobDescription"
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                placeholder="Enter job description"
                rows={3}
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="jobLocation">Job Location</Label>
              <GooglePlacesAddress
                id="jobLocation"
                value={jobLocation}
                onChange={setJobLocation}
                placeholder="Enter job location with Google Places autocomplete"
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Job Status</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">Priority Level</Label>
              <Select value={selectedPriority} onValueChange={setSelectedPriority}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tradesman">Tradesman Assignment</Label>
              <Select 
                value={selectedTradesmanId || (job?.tradesman_profile ? "assigned" : "unassigned")} 
                onValueChange={setSelectedTradesmanId}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Not Assigned</SelectItem>
                  {job?.tradesman_profile && (
                    <SelectItem value="assigned">Keep Current Assignment</SelectItem>
                  )}
                  <SelectItem value="unassign">Remove Assignment</SelectItem>
                  {tradesmen.map((tradesman) => (
                    <SelectItem key={tradesman.id} value={tradesman.id}>
                      {tradesman.full_name} {tradesman.company_name && `(${tradesman.company_name})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="agreedCost">Agreed Cost (£)</Label>
              <Input
                id="agreedCost"
                type="number"
                step="0.01"
                min="0"
                value={agreedCost}
                onChange={(e) => setAgreedCost(e.target.value)}
                placeholder="Enter agreed cost"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="requiredDate">Required Date</Label>
              <Input
                id="requiredDate"
                type="date"
                value={requiredDate}
                onChange={(e) => setRequiredDate(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="jobCategory">Job Category</Label>
              <Input
                id="jobCategory"
                value={jobCategory}
                onChange={(e) => setJobCategory(e.target.value)}
                placeholder="e.g., Plumbing, Electrical, etc."
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="requiredSpecialties">Required Specialties</Label>
            <Textarea
              id="requiredSpecialties"
              value={requiredSpecialties}
              onChange={(e) => setRequiredSpecialties(e.target.value)}
              placeholder="Enter specialties separated by commas (e.g., Gas Safe Certified, Emergency Callout, etc.)"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter className="flex justify-between">
          <Button
            variant="destructive"
            onClick={handleDeleteJob}
            disabled={isDeleting}
            className="flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            {isDeleting ? "Deleting..." : "Delete Job"}
          </Button>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleStatusUpdate}
              disabled={isUpdating}
              className="flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              {isUpdating ? "Saving Changes..." : "Save All Changes"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}